import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { action, computed, observable } from 'mobx'
import { task, type Task } from 'mobx-task'
import { filter, map, type Observable, tap } from 'rxjs'
import { throttle } from 'lodash-es'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { filterNonNil } from '@/utils/rx-util'
import {
  type OCRScopeWork,
  type OCRScopeWorkEvent,
} from '@/features/jobs/scoping/OCRScopeWork'
import { type OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import type { OCRScopeWorkAggregation } from './ocr-scope-work-aggregation'
import type {
  OCRScopeWorkStatus,
  OCRScopeWorkStatusFilterId,
} from './ocr-scope-work-status'
import { mapStatusToStatusNameResource } from './ocr-scope-work-mappers'
import { OCRScopeWorkWorkspaceSearchViewModel } from './OCRScopeWorkWorkspaceSearchViewModel'
import { Id } from '@/infra/id'
import { type Member } from '@/features/iam/members/Member'
import { type OCRScopingJobsPermissionViewModel } from '@/features/jobs/scoping/OCRScopingJobsPermissionViewModel'
import type { AlertFactory, AlertRetryWrapperFactory } from '@/features/shell'
import { type DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { OCRScopingJobBulkActionViewModel } from './OCRScopingJobBulkActionViewModel'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'

export type OCRScopeWorkWorkspaceFilter = WorkspaceOCRScopeWork | AllWorkspaces

export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

export interface WorkspaceOCRScopeWork {
  type: 'Workspace'
  workspace: Workspace
}

/**
 * A group of scope work statuses the list can be filtered by.
 */
export interface OCRScopeWorkStatusFilter {
  id: OCRScopeWorkStatusFilterId
  title: string
  subtitle: string
  headerName: string
  bulkAction: OCRScopeWorkBulkAction | null
  statuses: OCRScopeWorkStatus[]
  count: number
}

/**
 * An action that can be taken on the selected scope works of a status filter.
 */
export interface OCRScopeWorkBulkAction {
  name: string
  task: Task<[], Promise<void>>
  confirm: () => Promise<void>
}

const PAGE_SIZE = 30

/**
 * The scope work list, with its status and workspace filters.
 */
export class OCRScopingJobSidePanelViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The member whose workspace was last selected for filtering.
   */
  private _filteredMember: Member | null = null

  /**
   * Throttled, so the trailing call's promise reaches no caller and must
   * handle its own failure.
   */
  private readonly _refreshAggregations = throttle(
    () => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading the scoping counts failed',
        this._lifecycle.signal,
      )
      withRetry(() => this._fetchScopingAggregations()).catch(() => undefined)
    },
    600,
    { leading: true, trailing: true },
  )

  readonly workspaceSelectorVm

  readonly searchText = new SearchTextViewModel()

  readonly scroller = new InfiniteScrollProvider<OCRScopeWork>({
    limit: PAGE_SIZE,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading scope works failed',
        signal,
      )
      return withRetry(() =>
        this._ocrScopeWorkStore.listScopeWorks({
          workspaceId: this.selectedWorkspaceId,
          statuses: this.selectedStatusFilter?.statuses,
          searchText: this.searchText.value,
          limit: PAGE_SIZE,
          after: paging.after,
          signal,
        }),
      )
    },
  })

  readonly bulkActionVm

  @observable accessor aggregations: OCRScopeWorkAggregation[] = []

  @observable accessor showSearchField = false

  @observable accessor currentMemberWorkspaceId: Id = Id.none

  @observable
  accessor selectedStatusFilterId: OCRScopeWorkStatusFilterId | null = null

  @observable
  accessor workspaceFilter: OCRScopeWorkWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  constructor(
    private readonly _ocrScopingJobsPermissionVm: OCRScopingJobsPermissionViewModel,
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _currentMember$: Observable<Member>,
    newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.workspaceSelectorVm = new OCRScopeWorkWorkspaceSearchViewModel(
      this._getCurrentUserWorkspaceMemberships,
    )

    this.bulkActionVm = new OCRScopingJobBulkActionViewModel(
      this._ocrScopingJobsPermissionVm,
      this._ocrScopeWorkStore,
      this._draftJobStore,
      this.scroller,
      newAlert,
    )

    this._lifecycle
      .manage(this.workspaceSelectorVm)
      .manage(
        this._currentMember$.pipe(
          // Re-activating keeps the workspace the user picked; only another
          // member resets it.
          filter((member) => member !== this._filteredMember),
          tapAction((member) => {
            this._filteredMember = member
            this.currentMemberWorkspaceId = member.workspace.id

            if (
              member.workspace !== Workspace.none &&
              this.canViewScopingJobs
            ) {
              this.selectWorkspaceForFiltering(member.workspace)
            }
          }),
        ),
      )
      .manage(
        toStream(() => this.workspaceFilter).pipe(
          tap(() => {
            this._refreshItems()
            this._refreshAggregations()
          }),
        ),
      )
      .manage(
        this.searchText.debouncedValue$.pipe(tap(() => this._refreshItems())),
      )
      .manage(
        this._ocrScopeWorkStore.events$.pipe(
          tap(() => this._refreshAggregations()),
        ),
      )
      .manage(
        syncInfiniteScrollerItems({
          scroller: this.scroller,
          insertAt: 'start',
          additions$: this._ocrScopeWorkStore.events$.pipe(
            map((event) => this._addedScopeWork(event)),
            filterNonNil(),
          ),
          removals$: this._ocrScopeWorkStore.events$.pipe(
            map((event) => this._removedScopeWork(event)),
            filterNonNil(),
          ),
        }).pipe(
          filter((change) => change.type === 'remove'),
          tap((change) => this.bulkActionVm.deselectItem(change.item)),
        ),
      )
  }

  @computed
  get canViewScopingJobs() {
    return this._ocrScopingJobsPermissionVm.canViewScopingJobs
  }

  @computed
  get canManageOCRScopingJobs() {
    return this._ocrScopingJobsPermissionVm.canManageOCRScopingJobs
  }

  @computed
  get workspaces() {
    return this.workspaceSelectorVm.filteredWorkspaces
  }

  @computed
  get ocrScopeWorkJobs() {
    return this.scroller.items
  }

  @computed
  get statusFilterOptions(): OCRScopeWorkStatusFilter[] {
    const { bulkActionVm } = this
    return [
      {
        id: 'ReadyToSubmit',
        title: 'Ready to submit',
        subtitle: 'Jobs that are for submission',
        headerName: 'Job name',
        bulkAction: {
          name: 'Submit',
          task: bulkActionVm.bulkSubmit,
          confirm: bulkActionVm.confirmBulkSubmit,
        },
        statuses: ['ReadyToSubmit'],
        count: this._countOf('ReadyToSubmit'),
      },
      {
        id: 'UnderReview',
        title: this.canManageOCRScopingJobs
          ? 'Ready for review'
          : 'Review required',
        subtitle: 'Manual review required',
        headerName: 'Job name',
        bulkAction: this.canManageOCRScopingJobs
          ? {
              name: 'Set As Ready To Submit',
              task: bulkActionVm.bulkReview,
              confirm: bulkActionVm.confirmBulkReview,
            }
          : null,
        statuses: ['ReviewRequired', 'ReviewSuggested'],
        count:
          this._countOf('ReviewSuggested') + this._countOf('ReviewRequired'),
      },
      {
        id: 'ErrorOccurred',
        title: 'Errors',
        subtitle: 'View scoping errors',
        headerName: 'File name',
        bulkAction: this.canManageOCRScopingJobs
          ? {
              name: 'Rerun OCR',
              task: bulkActionVm.bulkRerun,
              confirm: bulkActionVm.confirmBulkRerun,
            }
          : null,
        statuses: ['ErrorOccurred'],
        count: this._countOf('ErrorOccurred'),
      },
      {
        id: 'ScopingInProgress',
        title: 'Processing',
        subtitle: 'Scoping in progress',
        headerName: 'File name',
        bulkAction: null,
        statuses: ['ScopingInProgress'],
        count: this._countOf('ScopingInProgress'),
      },
    ]
  }

  @computed
  get selectedStatusFilter() {
    return (
      this.statusFilterOptions.find(
        (f) => f.id === this.selectedStatusFilterId,
      ) ?? null
    )
  }

  /**
   * The workspace to list scope works of, or {@link Id.none} for all
   * workspaces.
   */
  @computed
  get selectedWorkspaceId(): Id {
    if (
      (this.workspaceFilter.type === 'AllWorkspaces' ||
        this.workspaceFilter.workspace === Workspace.none) &&
      this.canManageOCRScopingJobs
    ) {
      return Id.none
    }

    if (
      this.workspaceFilter.type === 'Workspace' &&
      this.workspaceFilter.workspace.id
    ) {
      return this.workspaceFilter.workspace.id
    }

    return Id.assert(this.currentMemberWorkspaceId)
  }

  @action.bound
  setStatusFilter(id: OCRScopeWorkStatusFilterId | null) {
    if (this.selectedStatusFilterId === id) {
      return
    }

    this.selectedStatusFilterId = id
    this.bulkActionVm.setStatusFilter(this.selectedStatusFilter)
    this._refreshAggregations()
    this._refreshItems()
  }

  @action.bound
  clearStatusFilter() {
    this.setStatusFilter(null)
  }

  @action.bound
  toggleSearchField() {
    this.showSearchField = !this.showSearchField
    if (!this.showSearchField) {
      this.searchText.clear()
    }
  }

  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'Workspace' }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load with the task's own state.
    this.fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.searchText.clear()
  }

  fetch = task(async () => {
    await this._fetchScopingAggregations()
  })

  private _countOf(status: OCRScopeWorkStatus) {
    const statusName = mapStatusToStatusNameResource(status)
    return (
      this.aggregations
        .find((a) => a.field === 'status_name')
        ?.buckets.find((b) => b.value === statusName)?.count ?? 0
    )
  }

  private _belongsToSelectedWorkspace(scopeWork: OCRScopeWork) {
    return (
      this.selectedWorkspaceId === Id.none ||
      this.selectedWorkspaceId === scopeWork.workspaceId
    )
  }

  /**
   * The scope work to add to the list after the event, if any.
   */
  private _addedScopeWork(event: OCRScopeWorkEvent) {
    if (event.type === 'Archived' || event.type === 'Submitted') {
      return null
    }

    const { scopeWork } = event
    if (!this._belongsToSelectedWorkspace(scopeWork)) {
      return null
    }

    switch (event.type) {
      case 'Created':
        return this.selectedStatusFilterId === 'ScopingInProgress' &&
          this.searchText.value === ''
          ? scopeWork
          : null
      case 'Updated':
        return this.selectedStatusFilter?.statuses.includes(scopeWork.status)
          ? scopeWork
          : null
      case 'NeedsAttention':
        return this.selectedStatusFilterId === 'UnderReview' ? scopeWork : null
      case 'Errored':
        return this.selectedStatusFilterId === 'ErrorOccurred'
          ? scopeWork
          : null
    }
  }

  /**
   * The listed scope work to remove from the list after the event, if any.
   */
  private _removedScopeWork(event: OCRScopeWorkEvent) {
    switch (event.type) {
      case 'Archived':
        return this._listed(event.scopeWorkId)
      case 'Submitted':
        return this._listed(event.scopeWork.id)
      case 'Created':
        return null
      case 'Updated':
        return this._belongsToSelectedWorkspace(event.scopeWork) &&
          !this.selectedStatusFilter?.statuses.includes(event.scopeWork.status)
          ? this._listed(event.scopeWork.id)
          : null
      case 'NeedsAttention':
        return this._belongsToSelectedWorkspace(event.scopeWork) &&
          this.selectedStatusFilterId !== 'UnderReview'
          ? this._listed(event.scopeWork.id)
          : null
      case 'Errored':
        return this._belongsToSelectedWorkspace(event.scopeWork) &&
          this.selectedStatusFilterId !== 'ErrorOccurred'
          ? this._listed(event.scopeWork.id)
          : null
    }
  }

  private _listed(scopeWorkId: Id) {
    return this.scroller.items.find((i) => i.id === scopeWorkId) ?? null
  }

  private async _fetchItems() {
    if (!this.selectedStatusFilter) {
      return
    }
    await this.scroller.fetch({ signal: this._lifecycle.signal })
  }

  private async _fetchScopingAggregations() {
    const aggregations = await this._ocrScopeWorkStore.fetchAggregations({
      workspaceId: this.selectedWorkspaceId,
      signal: this._lifecycle.signal,
    })
    this._setAggregations(aggregations)
  }

  @action.bound
  private _setAggregations(aggregations: OCRScopeWorkAggregation[]) {
    this.aggregations = aggregations
  }

  private _refreshItems() {
    // The retry alert already reported a failure.
    this._fetchItems().catch(() => undefined)
  }
}
